import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Trash2 } from 'lucide-react';

export default function YouTubeTrends() {
  const [channels, setChannels] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelUrl, setNewChannelUrl] = useState('');
  const [expanded, setExpanded] = useState({}); // channelId -> boolean
  const [player, setPlayer] = useState({ open: false, url: '', title: '' });
  const [sovExpanded, setSovExpanded] = useState({});
  const [sovYearExpanded, setSovYearExpanded] = useState({});
  const [sov6Expanded, setSov6Expanded] = useState({});
  const [detail, setDetail] = useState({ open: false, channel: null, monthly: [] });
  const [yearlyMap, setYearlyMap] = useState({}); // channelId -> { [year]: totalViews }
  const [monthly6Map, setMonthly6Map] = useState({}); // channelId -> { 'YYYY-MM': totalViews }
  const [yearlyListMap, setYearlyListMap] = useState({}); // channelId -> { [year]: Video[] }
  const [monthly6ListMap, setMonthly6ListMap] = useState({}); // channelId -> { 'YYYY-MM': Video[] }
  const [uploadsMonthlyMap, setUploadsMonthlyMap] = useState({}); // channelId -> monthIndex->count (current year)
  const [subsMonthlyMap, setSubsMonthlyMap] = useState({}); // channelId -> monthIndex->delta (current year)
  const [totalViewsGainsMap, setTotalViewsGainsMap] = useState({}); // channelId -> { 'YYYY-MM-DD': monthly_total_views }
  const [videoCountGainsMap, setVideoCountGainsMap] = useState({}); // channelId -> { 'YYYY-MM-DD': monthly_video_count }
  const [subsGainsMap, setSubsGainsMap] = useState({}); // channelId -> { 'YYYY-MM-DD': monthly_subscribers }
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteState, setDeleteState] = useState({ open: false, step: 1, channel: null });
  const [videoLoadingMap, setVideoLoadingMap] = useState({}); // channelId -> boolean
  const [videoHasMoreMap, setVideoHasMoreMap] = useState({}); // channelId -> boolean
  const [cpvOverrides, setCpvOverrides] = useState({}); // { 'YYYY-MM-01': { low, high } } - user edits
  const [yearlyCpvOverrides, setYearlyCpvOverrides] = useState({}); // { YYYY: { low, high } } for Share of Voice yearly cost estimation

  // Turkey YouTube CPV seasonality (₺/view) - low/high per calendar month for 2026
  // Q4 (BFCM/yılbaşı) peaks, Q1 troughs. Centered around ~₺0.18 yearly avg.
  const defaultCpvForMonth = (monthIndex) => {
    const table = [
      [0.12, 0.18], // Jan - off-season
      [0.12, 0.18], // Feb
      [0.13, 0.20], // Mar
      [0.13, 0.20], // Apr
      [0.15, 0.22], // May
      [0.15, 0.22], // Jun
      [0.12, 0.20], // Jul - summer dip
      [0.14, 0.22], // Aug
      [0.16, 0.24], // Sep - back-to-school
      [0.18, 0.26], // Oct
      [0.22, 0.32], // Nov - BFCM
      [0.25, 0.38], // Dec - yılbaşı peak
    ];
    const [low, high] = table[monthIndex] || [0.15, 0.22];
    return { low, high };
  };

  const getCpvForKey = (key) => {
    if (cpvOverrides[key]) return cpvOverrides[key];
    const d = new Date(key);
    return defaultCpvForMonth(d.getUTCMonth());
  };

  const setCpvField = (key, field, value) => {
    const current = getCpvForKey(key);
    setCpvOverrides(prev => ({
      ...prev,
      [key]: { ...current, [field]: Number(value) || 0 }
    }));
  };

  // Yearly CPV defaults (TR YouTube market, annual averages, TL inflation-adjusted)
  // Anchored to 2026 ≈ ₺0.18/view midpoint (max ~₺0.20); years roll back with
  // ~30-50% inflation (steeper 2022-2024 during TL devaluation).
  const defaultCpvForYear = (year) => {
    const table = {
      2019: [0.02, 0.04],
      2020: [0.03, 0.05],
      2021: [0.04, 0.06],
      2022: [0.05, 0.08],
      2023: [0.07, 0.11],
      2024: [0.10, 0.15],
      2025: [0.13, 0.18],
      2026: [0.15, 0.20],
    };
    const [low, high] = table[year] || [0.10, 0.15];
    return { low, high };
  };

  const getCpvForYear = (year) => yearlyCpvOverrides[year] || defaultCpvForYear(year);

  const setYearlyCpvField = (year, field, value) => {
    const current = getCpvForYear(year);
    setYearlyCpvOverrides(prev => ({
      ...prev,
      [year]: { ...current, [field]: Number(value) || 0 }
    }));
  };

  const formatCompact = (n) => {
    const v = Math.round(Number(n) || 0);
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.', ',') + 'M';
    if (v >= 1_000) return Math.round(v / 1_000) + 'B';
    return v.toString();
  };

  // Fetch channels linked to current brand
  const loadChannels = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      const resp = await fetch('https://api.glowytics.com/api/google-ads/youtube/channels', {
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const arr = (data?.channels || []).map((c) => ({
        name: c.title || c.handle || 'Channel',
        channelId: c.platform_channel_id || c.id,
        url: c.channel_url || (c.handle ? `https://www.youtube.com/${c.handle}` : (c.platform_channel_id ? `https://www.youtube.com/channel/${c.platform_channel_id}` : '#')),
        handle: c.handle || '',
        subscribers: Number(c.subscriber_count || 0),
        viewCount: Number(c.view_count || 0),
        videoCount: Number(c.video_count || 0),
        publishedAt: c.published_at || null,
        description: c.description || '',
        thumbnailUrl: (() => {
          try {
            const th = c.thumbnails || {};
            return th?.high?.url || th?.medium?.url || th?.default?.url || '';
          } catch(_) { return ''; }
        })(),
        videos: [],
        shorts: [],
      }));
      setChannels(arr);
    } catch (_) {}
  };

  // Initial load
  useEffect(() => {
    loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const format = (n) => Number(n).toLocaleString('tr-TR');
  const withTotals = useMemo(() => channels.map(ch => ({
    ...ch,
    totalViews: (typeof ch.viewCount === 'number') ? ch.viewCount : ch.videos.reduce((s, v) => s + v.views, 0),
    videoCount: typeof ch.videoCount === 'number' ? ch.videoCount : ch.videos.length,
  })), [channels]);

  const filtered = withTotals.filter(ch => ch.name.toLowerCase().includes(query.toLowerCase()));

  const getBrandId = () => {
    // Prefer the same key Overview2 uses to avoid auth issues
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    // Fallback common keys
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    // JSON blobs that may contain { id: "..." }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    // URL path fallback: /brand/<uuid>/...
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  };

  const addChannel = async () => {
    setErrorMsg('');
    if (!newChannelUrl && !newChannelName) {
      setErrorMsg('Please enter channel URL/handle and name');
      return;
    }
    const brandId = getBrandId();
    if (!brandId) {
      setErrorMsg('Brand not selected');
      return;
    }
    setIsSaving(true);
    try {
      const resp = await fetch('https://api.glowytics.com/api/google-ads/youtube/channel/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
        body: JSON.stringify({ channelUrl: newChannelUrl, channelName: newChannelName, brandId }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || 'Failed');
      // Refresh list to include enriched metadata (thumbnail/description)
      await loadChannels();
      setNewChannelName('');
      setNewChannelUrl('');
      setIsAddOpen(false);
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to add channel');
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (channel) => {
    setDeleteState({ open: true, step: 1, channel });
  };

  const performDelete = async () => {
    if (!deleteState?.channel) return;
    const brandId = getBrandId();
    if (!brandId) return;
    try {
      setIsSaving(true);
      const channelKey = deleteState.channel.channelId; // UC... or uuid
      const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/${encodeURIComponent(channelKey)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');
      setChannels(prev => prev.filter(c => c.channelId !== deleteState.channel.channelId));
      setDeleteState({ open: false, step: 1, channel: null });
    } catch (e) {
      setErrorMsg(e?.message || 'Delete failed');
    } finally {
      setIsSaving(false);
    }
  };

  const getEmbedUrl = (rawUrl) => {
    try {
      if (!rawUrl) return '';
      if (rawUrl.includes('watch?v=')) {
        const id = new URL(rawUrl).searchParams.get('v');
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
      }
      if (rawUrl.includes('/shorts/')) {
        const id = rawUrl.split('/shorts/')[1].split(/[?&]/)[0];
        // Embed shorts as normal video embed
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${id}`;
      }
      return rawUrl;
    } catch (_) {
      return rawUrl;
    }
  };

  const getVideoId = (rawUrl) => {
    try {
      if (rawUrl.includes('watch?v=')) return new URL(rawUrl).searchParams.get('v');
      if (rawUrl.includes('/shorts/')) return rawUrl.split('/shorts/')[1].split(/[?&]/)[0];
      return '';
    } catch (_) { return ''; }
  };

  const getThumb = (rawUrl) => {
    const id = getVideoId(rawUrl);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '/youtube.svg';
  };

  const formatDuration = (sec = 0) => {
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    const pad = (n) => n.toString().padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
  };

  const monthLabels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const generateMonthlyFromTotal = (seedKey, total) => {
    const arr = Array.from({ length: 12 }, (_, i) => {
      const seed = (i + seedKey.length * 13) * (seedKey.charCodeAt(0) % 11 + 5);
      return 0.6 + Math.abs(Math.sin(seed)) + (i % 3 === 0 ? 0.2 : 0);
    });
    const sum = arr.reduce((a, b) => a + b, 0) || 1;
    return arr.map(x => Math.round((x / sum) * total));
  };

  const generateMonthlyForVideo = (video) => {
    const total = video.views || 0;
    const seedKey = video.title || 'video';
    const arr = generateMonthlyFromTotal(seedKey, total);
    try {
      const m = new Date(video.uploaded).getMonth();
      for (let i = 0; i < 12; i++) if (i < m) arr[i] = 0; // no views before upload month (approx)
    } catch (_) {}
    return arr;
  };

  // Fetch yearly aggregates per channel using the same videos endpoint (paginate)
  const fetchYearlyForChannel = async (channelId) => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      const limit = 1000; // use large page size to reduce requests
      let offset = 0; let aggregated = {}; let lists = {};
      for (;;) {
        const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/videos?channel=${encodeURIComponent(channelId)}&limit=${limit}&offset=${offset}&orderBy=published_at&direction=desc`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (!resp.ok) break;
        const data = await resp.json();
        const rows = data?.videos || [];
        for (const v of rows) {
          const y = v.published_at ? new Date(v.published_at).getFullYear() : null;
          if (!y) continue;
          const views = Number(v.views || 0);
          aggregated[y] = (aggregated[y] || 0) + views;
          const item = {
            id: v.video_id,
            title: v.title || '',
            views: views,
            url: `https://www.youtube.com/watch?v=${v.video_id}`,
            uploaded: v.published_at ? new Date(v.published_at).toISOString().slice(0,10) : ''
          };
          if (!lists[y]) lists[y] = [];
          lists[y].push(item);
        }
        if (rows.length < limit) break;
        offset += rows.length;
      }
      setYearlyMap(prev => ({ ...prev, [channelId]: aggregated }));
      // sort lists by views desc and limit to 50 per year for rendering sanity
      const sorted = {};
      Object.keys(lists).forEach(y => {
        sorted[y] = lists[y].sort((a,b) => (b.views||0)-(a.views||0)).slice(0,50);
      });
      setYearlyListMap(prev => ({ ...prev, [channelId]: sorted }));
    } catch (_) { /* noop */ }
  };

  // Fetch last 6 months aggregates (by published_at month)
  const fetchMonthly6ForChannel = async (channelId) => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      const now = new Date();
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
      }
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1));
      const limit = 200;
      let offset = 0; let aggregated = {}; let lists = {};
      for (;;) {
        const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/videos?channel=${encodeURIComponent(channelId)}&limit=${limit}&offset=${offset}&orderBy=published_at&direction=desc`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (!resp.ok) break;
        const data = await resp.json();
        const rows = data?.videos || [];
        if (rows.length === 0) break;
        let reachedOlder = false;
        for (const v of rows) {
          const dt = v.published_at ? new Date(v.published_at) : null;
          if (!dt) continue;
          if (dt < start) { reachedOlder = true; continue; }
          const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
          const views = Number(v.views || 0);
          aggregated[key] = (aggregated[key] || 0) + views;
          const item = {
            id: v.video_id,
            title: v.title || '',
            views: views,
            url: `https://www.youtube.com/watch?v=${v.video_id}`,
            uploaded: v.published_at ? new Date(v.published_at).toISOString().slice(0,10) : ''
          };
          if (!lists[key]) lists[key] = [];
          lists[key].push(item);
        }
        if (rows.length < limit || reachedOlder) break;
        offset += rows.length;
      }
      // Ensure keys for last 6 months exist
      const filled = {};
      for (const m of months) filled[m] = aggregated[m] || 0;
      setMonthly6Map(prev => ({ ...prev, [channelId]: filled }));
      const sorted = {};
      Object.keys(lists).forEach(k => { sorted[k] = lists[k].sort((a,b) => (b.views||0)-(a.views||0)).slice(0,50); });
      setMonthly6ListMap(prev => ({ ...prev, [channelId]: sorted }));
    } catch (_) { /* noop */ }
  };

  // When channels list loads, fetch yearly aggregates for each
  useEffect(() => {
    if (!channels.length) return;
    (async () => {
      for (const ch of channels) {
        if (!yearlyMap[ch.channelId]) {
          await fetchYearlyForChannel(ch.channelId);
        }
        if (!monthly6Map[ch.channelId]) {
          await fetchMonthly6ForChannel(ch.channelId);
        }
        // Upload counts for current year
        try {
          const brandId = getBrandId();
          const y = new Date().getFullYear();
          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/uploads/monthly?channel=${encodeURIComponent(ch.channelId)}&year=${y}`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            const arr = new Array(12).fill(0);
            (data?.counts || []).forEach(r => {
              const mi = Math.max(1, Math.min(12, parseInt(r.month || 0, 10))) - 1;
              arr[mi] = parseInt(r.cnt || 0, 10);
            });
            setUploadsMonthlyMap(prev => ({ ...prev, [ch.channelId]: arr }));
          }
        } catch (_) {}
        // Subscriber growth monthly (last 12 months)
        try {
          const brandId = getBrandId();
          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/subscribers/monthly?channel=${encodeURIComponent(ch.channelId)}&months=12`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            // Map returned months to calendar month index for current year table
            const y = new Date().getFullYear();
            const arr = new Array(12).fill(0);
            (data?.items || []).forEach(r => {
              const d = new Date(r.month);
              if (d.getFullYear() === y) arr[d.getMonth()] = Number(r.monthly_subscribers || 0);
            });
            setSubsMonthlyMap(prev => ({ ...prev, [ch.channelId]: arr }));
          }
        } catch (_) {}
        // Total views gains monthly (inline SQL - no view required)
        try {
          const brandId = getBrandId();
          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/totalviews/monthly?channel=${encodeURIComponent(ch.channelId)}&months=12`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            // Map: month (YYYY-MM-DD) -> monthly_total_views
            const map = {};
            (data?.items || []).forEach(r => {
              const key = r.month ? new Date(r.month).toISOString().slice(0, 10) : null;
              if (key) map[key] = Number(r.monthly_total_views || 0);
            });
            setTotalViewsGainsMap(prev => ({ ...prev, [ch.channelId]: map }));
          }
        } catch (_) {}
        // Video count gains monthly (inline SQL - no view required)
        try {
          const brandId = getBrandId();
          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/videocount/monthly?channel=${encodeURIComponent(ch.channelId)}&months=12`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            const map = {};
            (data?.items || []).forEach(r => {
              const key = r.month ? new Date(r.month).toISOString().slice(0, 10) : null;
              if (key) map[key] = Number(r.monthly_video_count || 0);
            });
            setVideoCountGainsMap(prev => ({ ...prev, [ch.channelId]: map }));
          }
        } catch (_) {}
        // Subscriber gains monthly (inline SQL - no view required)
        try {
          const brandId = getBrandId();
          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/channel/subsgains/monthly?channel=${encodeURIComponent(ch.channelId)}&months=12`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            const map = {};
            (data?.items || []).forEach(r => {
              const key = r.month ? new Date(r.month).toISOString().slice(0, 10) : null;
              if (key) map[key] = Number(r.monthly_subscribers || 0);
            });
            setSubsGainsMap(prev => ({ ...prev, [ch.channelId]: map }));
          }
        } catch (_) {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  const openDetail = (channel) => {
    const monthly = generateMonthlyFromTotal(channel.channelId, channel.totalViews);
    setDetail({ open: true, channel, monthly });
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
        <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/youtubelogov2.svg" alt="YouTube" className="h-7 w-auto" />
          </div>
            <div className="flex items-center gap-2">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search channel"
                className="px-3 py-2 text-sm border rounded-lg"
              />
              <button className="px-3 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50" onClick={() => setIsAddOpen(true)}>+ Add channel</button>
            </div>
          </div>

          <div className="px-6 py-4 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'40%'}}>Channel</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'15%'}}>Videos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'20%'}}>Subscribers</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'20%'}}>Total views</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'5%'}}></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No channels yet. Use "Add channel" to link a YouTube channel.</td>
                    </tr>
                  )}
                  {filtered.map((ch, idx) => {
                    const isOpen = !!expanded[ch.channelId];
                    const onToggle = async () => {
                      const newOpen = !isOpen;
                      setExpanded(prev => ({ ...prev, [ch.channelId]: newOpen }));
                      if (newOpen && (!ch.videos || ch.videos.length === 0)) {
                        try {
                          const brandId = getBrandId();
                          if (!brandId) return;
                          setVideoLoadingMap(m => ({ ...m, [ch.channelId]: true }));
                          const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/videos?channel=${encodeURIComponent(ch.channelId)}&limit=16&orderBy=published_at&direction=desc`, {
                            headers: { 'X-Brand-ID': brandId },
                            credentials: 'include',
                          });
                          if (!resp.ok) return;
                          const data = await resp.json();
                          const vids = (data?.videos || []).map(v => ({
                            title: v.title || '',
                            url: `https://www.youtube.com/watch?v=${v.video_id}`,
                            views: Number(v.views || 0),
                            durationSec: Number(v.duration_seconds || 0),
                            uploaded: v.published_at ? new Date(v.published_at).toISOString().slice(0,10) : '',
                          }));
                          setChannels(prev => prev.map(item => item.channelId === ch.channelId ? { ...item, videos: vids } : item));
                          setVideoHasMoreMap(m => ({ ...m, [ch.channelId]: vids.length >= 16 }));
                        } catch (_) {}
                        finally {
                          setVideoLoadingMap(m => ({ ...m, [ch.channelId]: false }));
                        }
                      }
                    };
                    return (
                      <React.Fragment key={idx}>
                        <tr className="align-top hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm align-middle">
                            <div className="flex items-center gap-3">
                              <button
                                className="text-gray-500 hover:text-gray-700 inline-flex items-center justify-center"
                                onClick={onToggle}
                                title={isOpen ? 'Collapse' : 'Expand'}
                              >
                                <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                              </button>
                              <div className="relative">
                                <img
                                  src={ch.thumbnailUrl || '/youtube.svg'}
                                  alt="channel"
                                  className="w-8 h-8 rounded-full border border-gray-200 object-cover"
                                />
                                <img
                                  src="/youtube.svg"
                                  alt="yt"
                                  className="w-3.5 h-3.5 absolute -bottom-1 -right-1 bg-white rounded-full p-[2px] border border-gray-200"
                                />
                              </div>
                              <div className="min-w-0">
                                <a className="text-blue-600 hover:underline text-base sm:text-lg font-medium block truncate" href={ch.url} target="_blank" rel="noreferrer">{ch.name}</a>
                                {ch.handle ? (
                                  <div className="text-[11px] text-gray-500 truncate">{ch.handle}</div>
                                ) : null}
                                {ch.description ? (
                                  <div className="text-[11px] text-gray-400 truncate max-w-[420px]">{ch.description}</div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium">{format(ch.videoCount)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium">{format(ch.subscribers || 0)}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold">{format(ch.totalViews)}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <button
                              className="inline-flex p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                              title="Remove from this brand"
                              onClick={() => requestDelete(ch)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={5} className="px-4 pb-4">
                              <div className="grid grid-cols-1 gap-4">
                                <div className="mt-1">
                                  <div className="px-1 pb-2 text-sm flex items-center justify-between">
                                    <span className="font-medium text-gray-900">Latest videos</span>
                                    <span className="text-xs text-gray-500">{format(typeof ch.videoCount === 'number' ? ch.videoCount : ch.videos.length)} total</span>
                                  </div>
                                  <div
                                    className="grid grid-cols-1 md:grid-cols-2 gap-3 overflow-y-auto"
                                    style={{ height: '340px' }}
                                    onScroll={async (e) => {
                                      const el = e.currentTarget;
                                      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
                                      if (!nearBottom) return;
                                      if (videoLoadingMap[ch.channelId]) return;
                                      if (!videoHasMoreMap[ch.channelId]) return;
                                      try {
                                        const brandId = getBrandId();
                                        if (!brandId) return;
                                        setVideoLoadingMap(m => ({ ...m, [ch.channelId]: true }));
                                        const current = (channels.find(c => c.channelId === ch.channelId)?.videos || []).length;
                                        const resp = await fetch(`https://api.glowytics.com/api/google-ads/youtube/videos?channel=${encodeURIComponent(ch.channelId)}&limit=16&offset=${current}&orderBy=published_at&direction=desc`, {
                                          headers: { 'X-Brand-ID': brandId },
                                          credentials: 'include',
                                        });
                                        if (!resp.ok) return;
                                        const data = await resp.json();
                                        const more = (data?.videos || []).map(v => ({
                                          title: v.title || '',
                                          url: `https://www.youtube.com/watch?v=${v.video_id}`,
                                          views: Number(v.views || 0),
                                          durationSec: Number(v.duration_seconds || 0),
                                          uploaded: v.published_at ? new Date(v.published_at).toISOString().slice(0,10) : '',
                                        }));
                                        if (more.length > 0) {
                                          setChannels(prev => prev.map(item => item.channelId === ch.channelId ? { ...item, videos: [...item.videos, ...more] } : item));
                                        }
                                        setVideoHasMoreMap(m => ({ ...m, [ch.channelId]: more.length >= 16 }));
                                      } catch (_) {}
                                      finally {
                                        setVideoLoadingMap(m => ({ ...m, [ch.channelId]: false }));
                                      }
                                    }}
                                  >
                                    {ch.videos.length ? ch.videos.map((v, i) => (
                                      <button
                                        key={i}
                                        className="text-left rounded-lg border bg-white hover:border-gray-300 hover:shadow-sm transition w-full"
                                        onClick={() => setPlayer({ open: true, url: getEmbedUrl(v.url), title: v.title })}
                                      >
                                        <div className="flex items-center gap-3 p-2">
                                          <img src={getThumb(v.url)} alt="thumb" className="w-24 h-14 rounded object-cover flex-shrink-0 bg-gray-100" />
                                          <div className="min-w-0">
                                            <div className="font-medium text-sm leading-5 line-clamp-2">{v.title}</div>
                                            <div className="text-[11px] text-gray-500 mt-1">{format(v.views)} views • {formatDuration(v.durationSec)} • {v.uploaded}</div>
                                          </div>
                                        </div>
                                      </button>
                                    )) : (
                                      <div className="text-xs text-gray-400">No videos</div>
                                    )}
                                  </div>
                                  {videoLoadingMap[ch.channelId] ? (
                                    <div className="text-xs text-gray-500 mt-2 px-1">Loading…</div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Share of Voice - Yearly Views + estimated yearly ad spend */}
      <div className="max-w-6xl mx-auto mt-6">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="px-6 py-4 bg-white border-b">
            <div className="text-base font-medium text-gray-900">Share of Voice (Yearly Views) &amp; Tahmini Reklam Harcaması</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Tahmin = Yıllık view × CPV (low – high). Marka kanallarında view'lar pratikte tamamen paid varsayılır. Defaults: TR YouTube CPV yıllık bandı, TL enflasyonuna göre kademeli artar.
            </div>
          </div>
          <div className="px-6 py-4 overflow-x-auto bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {(() => {
                    // Last 8 years (ascending)
                    const nowYear = new Date().getFullYear();
                    const years = Array.from({ length: 8 }, (_, i) => nowYear - 7 + i);
                    (window).__ytYears = years;
                    return [
                      <th key="ch" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'18%'}}>Channel</th>,
                      ...years.map((y, i) => (
                        <th key={i} className="px-3 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">{y}</th>
                      ))
                    ];
                  })()}
                </tr>
                <tr className="bg-amber-50/40">
                  <th className="px-4 py-1 text-left text-[10px] font-normal text-gray-500">CPV ₺/view (low – high)</th>
                  {((window).__ytYears || []).map((y, i) => {
                    const cpv = getCpvForYear(y);
                    return (
                      <th key={i} className="px-2 py-1">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cpv.low}
                            onChange={(e) => setYearlyCpvField(y, 'low', e.target.value)}
                            className="w-12 px-1 py-0.5 text-[11px] border rounded text-right font-normal"
                          />
                          <span className="text-[10px] text-gray-400">–</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cpv.high}
                            onChange={(e) => setYearlyCpvField(y, 'high', e.target.value)}
                            className="w-12 px-1 py-0.5 text-[11px] border rounded text-right font-normal"
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {channels.map((ch, ri) => {
                  const years = (window).__ytYears || [];
                  const byYear = yearlyMap[ch.channelId] || {};
                  const isOpen = !!sovYearExpanded[ch.channelId];
                  return (
                    <React.Fragment key={ri}>
                      <tr className="cursor-pointer hover:bg-gray-50 align-top" onClick={() => setSovYearExpanded(prev => ({ ...prev, [ch.channelId]: !isOpen }))}>
                        <td className="px-4 py-2 text-gray-900">
                          <ChevronRight className={`w-4 h-4 inline-block mr-2 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <img src="/youtube.svg" alt="yt" className="inline-block w-5 h-5 mr-2 align-middle" />
                          <span>{ch.name}</span>
                        </td>
                        {years.map((y, ci) => {
                          const views = byYear[y] || 0;
                          const cpv = getCpvForYear(y);
                          const spendLow = views * cpv.low;
                          const spendHigh = views * cpv.high;
                          return (
                            <td key={ci} className="px-3 py-2 text-right">
                              <div className="text-gray-700">{formatCompact(views)}</div>
                              {views > 0 && (
                                <div className="text-[11px] text-emerald-600 font-medium mt-0.5">
                                  ₺{formatCompact(spendLow)}–₺{formatCompact(spendHigh)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {isOpen && (
                        <tr>
                          <td className="px-4 py-2 bg-gray-50" colSpan={years.length + 1}>
                            {(() => {
                              // Build pivot rows: unique videos across selected years
                              const lists = yearlyListMap[ch.channelId] || {};
                              const idToRow = new Map();
                              years.forEach(y => {
                                (lists[y] || []).forEach(v => {
                                  const key = v.id || v.url || v.title;
                                  if (!idToRow.has(key)) idToRow.set(key, { v, cols: {} });
                                  idToRow.get(key).cols[y] = v.views || 0;
                                });
                              });
                              const rows = Array.from(idToRow.values())
                                .sort((a,b) => (Object.values(b.cols).reduce((s,n)=>s+n,0)) - (Object.values(a.cols).reduce((s,n)=>s+n,0)))
                                .slice(0, 30); // show top 30 rows for readability
                              return (
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr>
                                      <th className="px-2 py-1 text-left font-medium" style={{width:'40%'}}>Video</th>
                                      {years.map((y,i3)=>(<th key={i3} className="px-2 py-1 text-right font-medium">{y}</th>))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((r,ri2)=> (
                                      <tr key={ri2} className="hover:bg-white">
                                        <td className="px-2 py-1 truncate"><a className="hover:underline" href={r.v.url} target="_blank" rel="noreferrer">{r.v.title}</a></td>
                                        {years.map((y,i4)=> (<td key={i4} className="px-2 py-1 text-right text-gray-700">{format(r.cols[y]||0)}</td>))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-gray-50 font-medium align-top">
                  <td className="px-4 py-2">TOTAL</td>
                  {((window).__ytYears || []).map((y, ci) => {
                    const totalViews = channels.reduce((s, ch) => s + ((yearlyMap[ch.channelId] || {})[y] || 0), 0);
                    const cpv = getCpvForYear(y);
                    const totalLow = totalViews * cpv.low;
                    const totalHigh = totalViews * cpv.high;
                    return (
                      <td key={ci} className="px-3 py-2 text-right">
                        <div>{formatCompact(totalViews)}</div>
                        {totalViews > 0 && (
                          <div className="text-[11px] text-emerald-700 mt-0.5">
                            ₺{formatCompact(totalLow)}–₺{formatCompact(totalHigh)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
            <div className="text-[11px] text-gray-500 mt-3 leading-relaxed">
              <strong>Yöntem:</strong> Marka YouTube kanallarında organik view (abonelik, arama, suggested) çok düşüktür — view'lar pratikte tamamen TrueView / in-stream reklam kampanyalarından gelir. Bu nedenle <em>view × CPV</em> doğrudan tahmini reklam harcamasını verir. CPV alanları her yıl için ayrı düzenlenebilir; defaults TL enflasyonuyla uyumlu yıllık ortalamayı yansıtır (2019 ~₺0.03, 2022 ~₺0.07, 2024 ~₺0.12, 2026 ~₺0.18).
            </div>
          </div>
        </div>
      </div>

      {/* Share of Voice - Last 6 Months (by published month) */}
      <div className="max-w-6xl mx-auto mt-6">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="px-6 py-4 bg-white border-b">
            <div className="text-base font-medium text-gray-900">Share of Voice (Last 6 Months)</div>
            <div className="text-xs text-gray-500">Channels × Months • values sum views for videos published in each month</div>
          </div>
          <div className="px-6 py-4 overflow-x-auto bg-white relative">
            {(() => {
              const now = new Date();
              const months = [];
              for (let i = 5; i >= 0; i--) {
                const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
                const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
                const label = `${monthLabels[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
                months.push({ key, label });
              }
              return (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'18%'}}>Channel</th>
                      {months.map((m, i) => (
                        <th key={i} className="px-3 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {channels.map((ch, ri) => {
                      const byMonth = monthly6Map[ch.channelId] || {};
                      const isOpen = !!sov6Expanded[ch.channelId];
                      return (
                        <React.Fragment key={ri}>
                          <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setSov6Expanded(prev => ({ ...prev, [ch.channelId]: !isOpen }))}>
                            <td className="px-4 py-2 text-gray-900">
                              <ChevronRight className={`w-4 h-4 inline-block mr-2 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                              <img src="/youtube.svg" alt="yt" className="inline-block w-5 h-5 mr-2 align-middle" />
                              <span>{ch.name}</span>
                            </td>
                            {months.map((m, ci) => (
                              <td key={ci} className="px-3 py-2 text-right text-gray-700">{format(byMonth[m.key] || 0)}</td>
                            ))}
                          </tr>
                          {isOpen && (
                            <tr>
                              <td className="px-4 py-2 bg-gray-50" colSpan={months.length + 1}>
                                {(() => {
                                  const lists = monthly6ListMap[ch.channelId] || {};
                                  const idToRow = new Map();
                                  months.forEach(m => {
                                    (lists[m.key] || []).forEach(v => {
                                      const key = v.id || v.url || v.title;
                                      if (!idToRow.has(key)) idToRow.set(key, { v, cols: {} });
                                      idToRow.get(key).cols[m.key] = v.views || 0;
                                    });
                                  });
                                  const rows = Array.from(idToRow.values())
                                    .sort((a,b) => (Object.values(b.cols).reduce((s,n)=>s+n,0)) - (Object.values(a.cols).reduce((s,n)=>s+n,0)))
                                    .slice(0, 30);
                                  return (
                                    <table className="min-w-full text-xs">
                                      <thead>
                                        <tr>
                                          <th className="px-2 py-1 text-left font-medium" style={{width:'40%'}}>Video</th>
                                          {months.map((m,i3)=>(<th key={i3} className="px-2 py-1 text-right font-medium">{m.label}</th>))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.map((r,ri2)=> (
                                          <tr key={ri2} className="hover:bg-white">
                                            <td className="px-2 py-1 truncate"><a className="hover:underline" href={r.v.url} target="_blank" rel="noreferrer">{r.v.title}</a></td>
                                            {months.map((m,i4)=> (<td key={i4} className="px-2 py-1 text-right text-gray-700">{format(r.cols[m.key]||0)}</td>))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      </div>


  {/* Monthly Total Views + Estimated Paid Spend (range) */}
  {true && (
  <div className="max-w-6xl mx-auto mt-6">
    <div className="rounded-2xl overflow-hidden border shadow-sm">
      <div className="px-6 py-4 bg-white border-b">
        <div className="text-base font-medium text-gray-900">Aylık Toplam İzlenme &amp; Tahmini Reklam Harcaması</div>
        <div className="text-xs text-gray-500 mt-0.5">
          Tahmin = Aylık view × CPV (low – high). Marka kanallarında view'lar pratikte tamamen paid (TrueView/in-stream) varsayılır. Defaults: Türkiye YouTube CPV bandı, Q4 yüksek / Q1 düşük.
        </div>
      </div>
      <div className="px-6 py-4 overflow-x-auto bg-white">
        {(() => {
          const now = new Date();
          const months = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
            const label = `${monthLabels[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
            months.push({ key, label });
          }
          return (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'16%'}}>Channel</th>
                  {months.map((m, i) => (
                    <th key={i} className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">{m.label}</th>
                  ))}
                </tr>
                <tr className="bg-amber-50/40">
                  <th className="px-3 py-1 text-left text-[10px] font-normal text-gray-500">CPV ₺/view (low – high)</th>
                  {months.map((m, i) => {
                    const cpv = getCpvForKey(m.key);
                    return (
                      <th key={i} className="px-2 py-1">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cpv.low}
                            onChange={(e) => setCpvField(m.key, 'low', e.target.value)}
                            className="w-12 px-1 py-0.5 text-[11px] border rounded text-right font-normal"
                          />
                          <span className="text-[10px] text-gray-400">–</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cpv.high}
                            onChange={(e) => setCpvField(m.key, 'high', e.target.value)}
                            className="w-12 px-1 py-0.5 text-[11px] border rounded text-right font-normal"
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {channels.map((ch, ri) => {
                  const viewsMap = totalViewsGainsMap[ch.channelId] || {};
                  return (
                    <tr key={ri} className="align-top">
                      <td className="px-3 py-2 text-gray-900">{ch.name}</td>
                      {months.map((m, ci) => {
                        const views = viewsMap[m.key] || 0;
                        const cpv = getCpvForKey(m.key);
                        const spendLow = views * cpv.low;
                        const spendHigh = views * cpv.high;
                        return (
                          <td key={ci} className="px-2 py-2 text-right">
                            <div className="text-gray-700 text-sm font-medium">{formatCompact(views)}</div>
                            <div className="text-[11px] text-emerald-600 font-medium mt-0.5">
                              ₺{formatCompact(spendLow)}–₺{formatCompact(spendHigh)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-medium align-top">
                  <td className="px-3 py-2">TOTAL</td>
                  {months.map((m, ci) => {
                    const totalViews = channels.reduce((s, ch) => {
                      const vm = totalViewsGainsMap[ch.channelId] || {};
                      return s + (vm[m.key] || 0);
                    }, 0);
                    const cpv = getCpvForKey(m.key);
                    const totalLow = totalViews * cpv.low;
                    const totalHigh = totalViews * cpv.high;
                    return (
                      <td key={ci} className="px-2 py-2 text-right">
                        <div>{formatCompact(totalViews)}</div>
                        <div className="text-[11px] text-emerald-700 mt-0.5">
                          ₺{formatCompact(totalLow)}–₺{formatCompact(totalHigh)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          );
        })()}
        <div className="text-[11px] text-gray-500 mt-3 leading-relaxed">
          <strong>Yöntem:</strong> Marka kanallarında organik view (abonelik, arama, suggested) çok düşüktür — view'ların pratikte tamamı TrueView / in-stream reklam kampanyalarından gelir. Bu nedenle <em>view × CPV</em> doğrudan tahmini reklam harcamasını verir. CPV alanları her ay için ayrı düzenlenebilir; defaults Türkiye YouTube CPV bandını mevsimsel olarak yansıtır (Q4 BFCM/yılbaşı yüksek, Q1 düşük).
        </div>
      </div>
    </div>
  </div>
  )}

  {/* Monthly Video Count - new videos uploaded each month */}
  {true && (
  <div className="max-w-6xl mx-auto mt-6">
    <div className="rounded-2xl overflow-hidden border shadow-sm">
      <div className="px-6 py-4 bg-white border-b">
        <div className="text-base font-medium text-gray-900">Monthly Video Count</div>
        <div className="text-xs text-gray-500">Channel × Months • how many new videos uploaded each month (from daily snapshots)</div>
      </div>
      <div className="px-6 py-4 overflow-x-auto bg-white">
        {(() => {
          const now = new Date();
          const months = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
            const label = `${monthLabels[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
            months.push({ key, label });
          }
          return (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'18%'}}>Channel</th>
                  {months.map((m, i) => (
                    <th key={i} className="px-3 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">{m.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {channels.map((ch, ri) => {
                  const countMap = videoCountGainsMap[ch.channelId] || {};
                  return (
                    <tr key={ri}>
                      <td className="px-4 py-2 text-gray-900">{ch.name}</td>
                      {months.map((m, ci) => (
                        <td key={ci} className="px-3 py-2 text-right text-gray-700">{format(countMap[m.key] || 0)}</td>
                      ))}
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2">TOTAL</td>
                  {months.map((m, ci) => {
                    const total = channels.reduce((s, ch) => {
                      const cm = videoCountGainsMap[ch.channelId] || {};
                      return s + (cm[m.key] || 0);
                    }, 0);
                    return <td key={ci} className="px-3 py-2 text-right">{format(total)}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  </div>
  )}

  {/* Monthly Subscriber Growth - subscriber gains each month */}
  {true && (
  <div className="max-w-6xl mx-auto mt-6">
    <div className="rounded-2xl overflow-hidden border shadow-sm">
      <div className="px-6 py-4 bg-white border-b">
        <div className="text-base font-medium text-gray-900">Monthly Subscriber Growth</div>
        <div className="text-xs text-gray-500">Channel × Months • how many new subscribers gained each month (from daily snapshots)</div>
      </div>
      <div className="px-6 py-4 overflow-x-auto bg-white">
        {(() => {
          const now = new Date();
          const months = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
            const label = `${monthLabels[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
            months.push({ key, label });
          }
          return (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width:'18%'}}>Channel</th>
                  {months.map((m, i) => (
                    <th key={i} className="px-3 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">{m.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {channels.map((ch, ri) => {
                  const subsMap = subsGainsMap[ch.channelId] || {};
                  return (
                    <tr key={ri}>
                      <td className="px-4 py-2 text-gray-900">{ch.name}</td>
                      {months.map((m, ci) => (
                        <td key={ci} className="px-3 py-2 text-right text-gray-700">{format(subsMap[m.key] || 0)}</td>
                      ))}
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2">TOTAL</td>
                  {months.map((m, ci) => {
                    const total = channels.reduce((s, ch) => {
                      const sm = subsGainsMap[ch.channelId] || {};
                      return s + (sm[m.key] || 0);
                    }, 0);
                    return <td key={ci} className="px-3 py-2 text-right">{format(total)}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  </div>
  )}

      {/* Page footer logo (below content) */}
      <div className="max-w-6xl mx-auto px-6 py-8 flex justify-end">
        <img
          src="/developed-with-youtube-sentence-case-dark.png"
          alt="Developed with YouTube"
          className="h-20 md:h-24 lg:h-28 opacity-90 pointer-events-none select-none"
        />
      </div>

      {player.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPlayer({ open: false, url: '', title: '' })}></div>
          <div className="relative bg-black rounded-2xl shadow-xl w-full max-w-4xl mx-4">
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 text-white rounded-t-2xl">
              <div className="text-sm font-medium truncate">{player.title}</div>
              <button className="text-gray-300 hover:text-white" onClick={() => setPlayer({ open: false, url: '', title: '' })}>✕</button>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                title={player.title}
                src={player.url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full rounded-b-2xl"
              />
            </div>
          </div>
        </div>
      )}

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsAddOpen(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-lg mx-4">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add YouTube Channel</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsAddOpen(false)}>✕</button>
            </div>
            <div className="p-5 space-y-3">
              <input
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Channel name"
              />
              <input
                value={newChannelUrl}
                onChange={(e) => setNewChannelUrl(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Channel URL (@handle or full URL)"
              />
              {errorMsg ? (
                <div className="text-xs text-red-600">{errorMsg}</div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <button className="px-3 py-2 rounded-lg border" onClick={() => setIsAddOpen(false)}>Cancel</button>
                <button
                  className="px-3 py-2 rounded-lg bg-black text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={addChannel}
                  disabled={isSaving || (!newChannelUrl && !newChannelName) || !getBrandId()}
                >
                  {isSaving ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteState({ open: false, step: 1, channel: null })}></div>
          <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-md mx-4 p-5">
            <div className="text-lg font-semibold mb-2">Remove channel</div>
            {deleteState.step === 1 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">You are about to unfollow “{deleteState.channel?.name}” for this brand. Continue?</p>
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border" onClick={() => setDeleteState({ open: false, step: 1, channel: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-black text-white" onClick={() => setDeleteState(s => ({ ...s, step: 2 }))}>Continue</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Are you sure? This will not delete the channel from the library; it only removes access for this brand.</p>
                {errorMsg ? <div className="text-xs text-red-600">{errorMsg}</div> : null}
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border" onClick={() => setDeleteState({ open: false, step: 1, channel: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-red-600 text-white disabled:opacity-60" disabled={isSaving} onClick={performDelete}>{isSaving ? 'Removing...' : 'Remove'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {detail.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail({ open: false, channel: null, monthly: [] })}></div>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-5xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div className="flex items-center gap-2">
                <img src="/youtube.svg" alt="yt" className="w-7 h-7" />
                <div className="font-medium">{detail.channel?.name}</div>
                <div className="text-xs text-gray-500">{detail.channel?.channelId}</div>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setDetail({ open: false, channel: null, monthly: [] })}>✕</button>
            </div>
            <div className="p-5">
              {/* Monthly views bar chart */}
              <div className="rounded-lg border mb-5">
                <div className="px-3 py-2 border-b font-medium">Monthly total views (last 12 months)</div>
                <div className="p-4">
                  <svg width="100%" height="220" viewBox="0 0 720 220" preserveAspectRatio="xMidYMid meet">
                    {(() => {
                      const width = 720, height = 200, pad = 30;
                      const values = detail.monthly;
                      const max = Math.max(...values, 1);
                      const barW = (width - pad * 2) / values.length - 8;
                      return (
                        <g transform={`translate(${pad},10)`}>
                          {values.map((v, i) => {
                            const h = (v / max) * (height - 40);
                            const x = i * ((width - pad * 2) / values.length);
                            const y = height - 20 - h;
                            return (
                              <g key={i}>
                                <rect x={x} y={y} width={barW} height={h} fill="#111827" rx="3" />
                                <text x={x + barW / 2} y={height - 5} fontSize="10" textAnchor="middle" fill="#6B7280">{monthLabels[i]}</text>
                              </g>
                            );
                          })}
                        </g>
                      );
                    })()}
                  </svg>
                </div>
              </div>

              {/* Video and shorts lists */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border">
                  <div className="px-3 py-2 border-b font-medium">Videos ({detail.channel?.videos.length || 0})</div>
                  <div className="p-3 space-y-2">
                    {(detail.channel?.videos || []).map((v, i) => (
                      <button key={i} className="p-2 rounded-lg border text-left group hover:border-gray-400 w-full" onClick={() => setPlayer({ open: true, url: getEmbedUrl(v.url), title: v.title })}>
                        <div className="flex items-center gap-3">
                          <img src={getThumb(v.url)} alt="thumb" className="w-16 h-9 rounded object-cover flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{v.title}</div>
                            <div className="text-xs text-gray-500">{format(v.views)} views • {v.uploaded}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border">
                  <div className="px-3 py-2 border-b font-medium">Shorts ({detail.channel?.shorts.length || 0})</div>
                  <div className="p-3 space-y-2">
                    {(detail.channel?.shorts || []).map((s, i) => (
                      <button key={i} className="p-2 rounded-lg border text-left group hover:border-gray-400 w-full" onClick={() => setPlayer({ open: true, url: getEmbedUrl(s.url), title: s.title })}>
                        <div className="flex items-center gap-3">
                          <img src={getThumb(s.url)} alt="thumb" className="w-16 h-9 rounded object-cover flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{s.title}</div>
                            <div className="text-xs text-gray-500">{format(s.views)} views • {s.uploaded}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


